import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, WhatsappLogo, ArrowLeft, Minus, Plus, Check } from '@phosphor-icons/react';
import { getItem, getItems } from '../utils/api';
import { formatPrice, generateWhatsAppURL } from '../utils/helpers';
import { useCartStore, useRecentlyViewedStore } from '../store';
import ProductCard from '../components/ProductCard';
import SocialShare from '../components/SocialShare';
import ProductReviews from '../components/ProductReviews';
import RecentlyViewed from '../components/RecentlyViewed';
import SEO from '../components/SEO';
import { toast } from 'sonner';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  
  const addItem = useCartStore((state) => state.addItem);
  const addToRecentlyViewed = useRecentlyViewedStore((state) => state.addItem);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await getItem(id);
        const productData = res.data.data;
        setProduct(productData);
        setSelectedImage(0);
        setQuantity(1);
        
        // Add to recently viewed
        if (productData) {
          addToRecentlyViewed(productData);
        }
        
        // Fetch related products
        if (productData?.category_name) {
          const relatedRes = await getItems({ 
            category: productData.category_name, 
            limit: 4 
          });
          setRelatedProducts(
            (relatedRes.data.data || []).filter(p => p.id !== parseInt(id))
          );
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProduct();
    window.scrollTo(0, 0);
  }, [id]);

  const handleAddToCart = () => {
    const availableStock = product.stock_quantity ?? product.quantity ?? 0;
    if (availableStock > 0 && quantity > availableStock) {
      toast.error(`Only ${availableStock} items available in stock`);
      return;
    }
    for (let i = 0; i < quantity; i++) {
      addItem(product);
    }
    toast.success(`${product.name} added to cart`);
  };

  const handleWhatsAppInquiry = () => {
    const url = generateWhatsAppURL([{ ...product, quantity }], product.selling_rate * quantity);
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-default py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-square bg-gray-200 animate-pulse" />
            <div className="space-y-6">
              <div className="h-8 bg-gray-200 rounded animate-pulse" />
              <div className="h-6 bg-gray-200 rounded w-1/3 animate-pulse" />
              <div className="h-24 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background-default flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-display text-2xl text-secondary mb-4">Product not found</h2>
          <Link to="/shop" className="btn-primary">
            Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  const images = product.image_urls?.length > 0 
    ? product.image_urls 
    : ['https://images.unsplash.com/photo-1736436789706-005f2218a96d?w=800'];

  return (
    <div className="min-h-screen bg-background-default">
      <SEO 
        title={product.name}
        description={product.description || `Buy ${product.name} at Luster Adorn. Premium quality imitation jewelry.`}
        image={images[0]}
        url={`/product/${product.id}`}
        type="product"
        product={product}
      />
      {/* Breadcrumb */}
      <div className="bg-background-subtle py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 font-body text-sm">
            <Link to="/" className="text-text-secondary hover:text-primary transition-colors">Home</Link>
            <span className="text-text-muted">/</span>
            <Link to="/shop" className="text-text-secondary hover:text-primary transition-colors">Shop</Link>
            {product.category_name && (
              <>
                <span className="text-text-muted">/</span>
                <Link 
                  to={product.category_id ? `/shop/${product.category_id}` : '/shop'} 
                  className="text-text-secondary hover:text-primary transition-colors"
                >
                  {product.category_name}
                </Link>
              </>
            )}
            <span className="text-text-muted">/</span>
            <span className="text-text-primary">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Product Details */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/shop" 
            data-testid="back-to-shop"
            className="inline-flex items-center gap-2 font-body text-sm text-text-secondary hover:text-primary transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            Back to Shop
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image Gallery */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Main Image */}
              <div className="aspect-square overflow-hidden mb-4">
                <img
                  src={images[selectedImage]}
                  alt={product.name}
                  data-testid="main-product-image"
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Thumbnail Gallery */}
              {images.length > 1 && (
                <div className="flex gap-3">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      data-testid={`thumbnail-${index}`}
                      className={`w-20 h-20 overflow-hidden border-2 transition-colors ${
                        selectedImage === index ? 'border-primary' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col"
            >
              {/* Category */}
              {product.category_name && (
                <span className="font-body text-sm text-primary uppercase tracking-widest mb-2">
                  {product.category_name}
                </span>
              )}
              
              {/* Name */}
              <h1 className="font-display text-3xl md:text-4xl text-secondary mb-4" data-testid="product-name">
                {product.name}
              </h1>
              
              {/* Price */}
              <p className="font-display text-3xl text-primary mb-2" data-testid="product-price">
                {formatPrice(product.selling_rate)}
                {product.mrp && product.mrp > product.selling_rate && (
                  <span className="text-text-muted line-through ml-3 text-xl font-normal">
                    {formatPrice(product.mrp)}
                  </span>
                )}
              </p>
              {product.mrp && product.mrp > product.selling_rate && (
                <p className="font-body text-sm text-green-600 mb-2">
                  Save {formatPrice(product.mrp - product.selling_rate)} ({Math.round((1 - product.selling_rate / product.mrp) * 100)}% off)
                </p>
              )}
              <p className="font-body text-green-600 text-sm mb-6">
                Free Shipping
              </p>
              
              {/* Description */}
              {product.description && (
                <div className="font-body text-text-secondary leading-relaxed mb-8">
                  {product.description.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="mb-4 whitespace-pre-line">
                      {paragraph.split('"').map((part, partIdx) => 
                        partIdx % 2 === 1 ? (
                          <span key={partIdx} className="italic text-primary font-medium">"{part}"</span>
                        ) : (
                          part
                        )
                      )}
                    </p>
                  ))}
                </div>
              )}
              
              {/* Product Details */}
              <div className="mb-8">
                <h3 className="font-display text-lg text-secondary mb-4">Product Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  {product.type && (
                    <div>
                      <p className="font-body text-xs uppercase tracking-wider text-text-secondary mb-1">Type</p>
                      <p className="font-body text-sm text-secondary">{product.type}</p>
                    </div>
                  )}
                  {product.design && (
                    <div>
                      <p className="font-body text-xs uppercase tracking-wider text-text-secondary mb-1">Design</p>
                      <p className="font-body text-sm text-secondary">{product.design}</p>
                    </div>
                  )}
                  {product.material && (
                    <div>
                      <p className="font-body text-xs uppercase tracking-wider text-text-secondary mb-1">Material</p>
                      <p className="font-body text-sm text-secondary">{product.material}</p>
                    </div>
                  )}
                  {product.closure_type && (
                    <div>
                      <p className="font-body text-xs uppercase tracking-wider text-text-secondary mb-1">Closure Type</p>
                      <p className="font-body text-sm text-secondary">{product.closure_type}</p>
                    </div>
                  )}
                </div>
                {product.styling_tip && (
                  <div className="mt-4 p-4 bg-background-subtle rounded-lg">
                    <p className="font-body text-xs uppercase tracking-wider text-text-secondary mb-1">Styling Tip</p>
                    <p className="font-body text-sm text-text-secondary italic">{product.styling_tip}</p>
                  </div>
                )}
              </div>
              
              {/* Availability */}
              <div className="flex items-center gap-2 mb-8">
                {(product.stock_quantity ?? product.quantity ?? 0) > 0 ? (
                  <>
                    <Check size={18} className="text-green-600" />
                    <span className="font-body text-sm text-green-600">In Stock</span>
                    {(product.stock_quantity ?? product.quantity ?? 0) < 5 && (
                      <span className="font-body text-xs text-orange-500">
                        (Only {product.stock_quantity ?? product.quantity ?? 0} left)
                      </span>
                    )}
                  </>
                ) : (
                  <span className="font-body text-sm text-red-500">Out of Stock</span>
                )}
              </div>
              
              {/* Quantity Selector */}
              {(product.stock_quantity ?? product.quantity ?? 0) > 0 && (
                <div className="flex items-center gap-4 mb-8">
                  <span className="font-body text-sm text-text-secondary">Quantity:</span>
                  <div className="flex items-center border border-border-default">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      data-testid="quantity-decrease"
                      className="p-3 hover:bg-background-subtle transition-colors"
                    >
                      <Minus size={16} />
                    </button>
                    <span className="px-6 font-body" data-testid="quantity-value">{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min((product.stock_quantity ?? product.quantity ?? 0), quantity + 1))}
                      data-testid="quantity-increase"
                      className="p-3 hover:bg-background-subtle transition-colors"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mt-auto">
                <button
                  onClick={handleAddToCart}
                  disabled={(product.stock_quantity ?? product.quantity ?? 0) === 0}
                  data-testid="add-to-cart-btn"
                  className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ShoppingBag size={20} />
                  Add to Cart
                </button>
                <button
                  onClick={handleWhatsAppInquiry}
                  data-testid="whatsapp-inquiry-btn"
                  className="whatsapp-btn flex-1 justify-center"
                >
                  <WhatsappLogo size={20} weight="fill" />
                  Buy on WhatsApp
                </button>
              </div>
              
              {/* Product Highlights */}
              {(product.highlights || product.key_features) && (
                <div className="mt-10 pt-8 border-t border-border-default">
                  <h3 className="font-display text-xl text-secondary mb-6">Product Highlights</h3>
                  <div className="grid grid-cols-1 gap-3">
                    {(product.highlights ? product.highlights.split('\n').filter(h => h.trim()) : 
                     product.key_features ? product.key_features : []).map((highlight, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <Check size={18} className="text-primary mt-0.5 flex-shrink-0" />
                        <p className="font-body text-sm text-text-secondary">{highlight}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Features */}
              <div className="mt-10 pt-8 border-t border-border-default">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-background-subtle">
                    <p className="font-body text-xs uppercase tracking-wider text-text-secondary mb-1">Quality</p>
                    <p className="font-display text-sm">Premium Finish</p>
                  </div>
                  <div className="text-center p-4 bg-background-subtle">
                    <p className="font-body text-xs uppercase tracking-wider text-text-secondary mb-1">Shipping</p>
                    <p className="font-display text-sm">Pan India</p>
                  </div>
                </div>
              </div>
              
              {/* Social Share */}
              <div className="mt-6 pt-6 border-t border-border-default">
                <SocialShare product={product} />
              </div>
            </motion.div>
          </div>
          
          {/* Product Reviews */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ProductReviews productId={product.id} />
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-16 bg-background-subtle">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-3xl text-secondary text-center mb-10">
              You May Also Like
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {relatedProducts.slice(0, 4).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* Recently Viewed */}
      <RecentlyViewed />
    </div>
  );
};

export default ProductDetail;
